import AppError from '@/components/AppError';
import { TappFormField } from '@/components/ui/TappFormField';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { Company, UpdateCompanyDto } from '@/lib/models/company';
import { ApiUtils } from '@/lib/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/components/ui/alert-dialog-provider';

const CompanyDetailsPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('editCompany')} defaultBack backLink="/admin/companies" />
      <Details />
    </>
  );
};

const Details = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();

  const companySchema = z.object({
    name: z.string().min(1, t('name') + ' ' + t('isRequired')),
    subaccountId: z.string().optional(),
    dayTariff: z
      .number({
        required_error: t('dayTariff') + ' ' + t('isRequired'),
        invalid_type_error: t('dayTariff') + ' ' + t('isRequired'),
      })
      .positive(t('dayTariff') + ' ' + t('mustBePositive'))
      .refine((val) => Number(val.toFixed(2)) === val, {
        message: t('dayTariff') + ' ' + t('maxTwoDecimals'),
      }),
    nightTariff: z
      .number({
        required_error: t('nightTariff') + ' ' + t('isRequired'),
        invalid_type_error: t('nightTariff') + ' ' + t('isRequired'),
      })
      .positive(t('nightTariff') + ' ' + t('mustBePositive'))
      .refine((val) => Number(val.toFixed(2)) === val, {
        message: t('nightTariff') + ' ' + t('maxTwoDecimals'),
      }),
  });

  type CompanyFormData = z.infer<typeof companySchema>;

  const { data, isLoading, error } = useQuery({
    queryKey: [`company/${id}`],
    queryFn: async () => {
      const response = await ApiUtils.get<Company>(`company/${id}`);
      return response;
    },
  });

  const { mutateAsync: updateCompany } = useMutation({
    mutationFn: (data: UpdateCompanyDto) => ApiUtils.patch(`company/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: [`company/${id}`] });
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      subaccountId: '',
      dayTariff: undefined,
      nightTariff: undefined,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        subaccountId: data.subaccountId,
        dayTariff: data.dayTariff,
        nightTariff: data.nightTariff,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formData: CompanyFormData) => {
    try {
      await updateCompany(formData);
      await alert({
        title: t('success'),
        body: t('companyUpdated'),
      });
      navigate('/admin/companies');
    } catch (err) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  if (error) return <AppError error={error} />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex w-full max-w-md flex-1 flex-col items-start gap-2', { isLoading })}
    >
      <TappFormField>
        <TappLabel htmlFor="name">{t('name')}</TappLabel>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('companyNamePlaceholder')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="subaccountId">{t('subaccountId')}</TappLabel>
        <Controller
          name="subaccountId"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('subaccountIdPlaceholder')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="dayTariff">{t('dayTariff')}</TappLabel>
        <Controller
          name="dayTariff"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput
              className="w-full"
              type="number"
              step="0.01"
              min="0.01"
              placeholder={t('dayTariffPlaceholder')}
              {...field}
              {...fieldState}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="nightTariff">{t('nightTariff')}</TappLabel>
        <Controller
          name="nightTariff"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput
              className="w-full"
              type="number"
              step="0.01"
              min="0.01"
              placeholder={t('nightTariffPlaceholder')}
              {...field}
              {...fieldState}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          )}
        />
      </TappFormField>

      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {t('save')}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate('/admin/companies')}>
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};

export default CompanyDetailsPage;
